import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface Slide {
  id: string;
  tag: string;
  title: string;
  desc: string;
  image: string;
  to: string;
}

const slides: Slide[] = [
  {
    id: 's1',
    tag: '热门推荐',
    title: '掌握 React 工程化，做真正能上线的项目',
    desc: '从组件化思维到构建部署，一线架构师带你完整走通现代前端开发流程。',
    image: 'https://readdy.ai/api/search-image?query=Abstract%20artistic%20technology%20background%20with%20deep%20blue%20gradient%20and%20subtle%20geometric%20code%20shapes%2C%20dark%20moody%20studio%20lighting%2C%20high%20contrast%20for%20white%20text%20overlay%2C%20modern%20minimal%20editorial%20style%2C%20clean%20and%20professional%2C%20high%20detail%20wide%20composition&width=1920&height=900&seq=hero-react-01&orientation=landscape',
    to: '/detail/react-frontend',
  },
  {
    id: 's2',
    tag: '专题精选',
    title: '雅思 · 日语 · 韩语，语言考级一次通关',
    desc: '应试经验丰富的讲师阵容，从题型技巧到真题实战，为你规划高效提分路线。',
    image: 'https://readdy.ai/api/search-image?query=Abstract%20artistic%20background%20of%20floating%20speech%20bubbles%20and%20letters%20in%20deep%20blue%20and%20teal%20tones%20over%20a%20dark%20gradient%2C%20moody%20soft%20lighting%2C%20strong%20contrast%20for%20white%20text%20overlay%2C%20elegant%20minimal%20design%2C%20high%20detail%20wide%20composition&width=1920&height=900&seq=hero-language-02&orientation=landscape',
    to: '/topics/language-exam-sprint',
  },
  {
    id: 's3',
    tag: '新课上线',
    title: '人工智能通识课，把大模型用进工作',
    desc: '理解原理、掌握提示词工程与常用 AI 工具，让智能能力真正为你所用。',
    image: 'https://readdy.ai/api/search-image?query=Abstract%20artistic%20representation%20of%20a%20neural%20network%20with%20glowing%20nodes%20over%20a%20deep%20navy%20blue%20gradient%20background%2C%20moody%20futuristic%20lighting%2C%20high%20contrast%20for%20white%20text%20overlay%2C%20clean%20minimal%20editorial%20style%2C%20high%20detail%20wide%20composition&width=1920&height=900&seq=hero-ai-03&orientation=landscape',
    to: '/detail/ai-intro',
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();

  const next = useCallback(() => setIndex((i) => (i + 1) % slides.length), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + slides.length) % slides.length), []);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const active = slides[index];

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : '/search');
  };

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="relative w-full h-[460px] sm:h-[520px] md:h-[600px]">
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              title={`${slide.title} · 秘密教学`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/60 to-primary-950/30"></div>
          </div>
        ))}

        <div className="relative z-10 h-full w-full flex items-center">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-2xl text-background-50">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs md:text-sm font-medium mb-4">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-fire-fill"></i>
                </span>
                {active.tag}
              </span>
              <h1 className="font-heading font-extrabold text-2xl md:text-4xl lg:text-5xl leading-tight mb-4 animate-fade-up">
                {active.title}
              </h1>
              <p className="text-sm md:text-base text-background-100/85 leading-relaxed mb-6 max-w-xl">
                {active.desc}
              </p>

              <form
                onSubmit={submitSearch}
                className="flex items-center bg-background-50 rounded-full p-1.5 mb-5 max-w-xl w-full"
              >
                <span className="w-9 h-9 flex items-center justify-center text-foreground-400 text-lg shrink-0">
                  <i className="ri-search-line"></i>
                </span>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="想学什么？试试搜索「英语」「Python」"
                  className="flex-1 bg-transparent border-0 outline-none text-sm text-foreground-900 placeholder:text-foreground-400 min-w-0"
                />
                <button
                  type="submit"
                  className="h-10 px-5 md:px-6 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  搜索课程
                </button>
              </form>

              <div className="flex items-center gap-3">
                <Link
                  to={active.to}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看详情
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
                <Link
                  to="/category"
                  className="inline-flex items-center h-11 px-6 rounded-md border border-background-100/50 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览全部课程
                </Link>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={prev}
          aria-label="上一张"
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50/85 text-foreground-900 hover:bg-background-50 text-xl cursor-pointer"
        >
          <i className="ri-arrow-left-s-line"></i>
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="下一张"
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50/85 text-foreground-900 hover:bg-background-50 text-xl cursor-pointer"
        >
          <i className="ri-arrow-right-s-line"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`切换到第 ${i + 1} 张`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? 'w-7 bg-accent-500' : 'w-3 bg-background-50/60 hover:bg-background-50'
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}